import { FC, useCallback, useMemo, useState } from 'react'
import { Form, Modal } from 'antd'
import { FormProps, useForm, useWatch } from 'antd/es/form/Form'
import amclasses from '@pages/Admin/CRM/styles/ammodal.module.css'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import sclasses from '../SetCourseClassDirectionModal/index.module.css'
import cn from 'classnames'
import type { LeadProfileLead } from '../../../../LeadProfileModal.types'
import { IcoRemove } from '@/utils/icons'
import { useAddAlert } from '@/hooks/useAddAlert'
import Button from '@UI/New/Button'
import { classApi, directionAPI, directionGroupApi } from '@/api'
import { ICustomClass } from '../SetCourseClassDirectionModal/SetClassModalBody'
import { useAsync, useAsyncRetry } from 'react-use'
import { ICustomCourse } from '../SetCourseClassDirectionModal/SetCourseModalBody'
import { useStreamCustomPeriodLinkForm } from './components/StreamCustomPeriodLinkFields/useStreamCustomPeriodLinkForm'
import { useStreamOneTimeLinkForm } from './components/StreamOneTimeLinkFields/useStreamOneTimeLinkForm'
import { useMonthlyCourseLinkForm } from './components/MonthlyCourseLinkFields/useMonthlyCourseLinkForm'
import type { CourseLinkGroupOption } from './components/CourseLinkGroupSelect/CourseLinkGroupSelect'
import { SHARED_BALANCE_GROUP_DISABLED_TOOLTIP } from './components/CourseLinkGroupSelect/CourseLinkGroupSelect'
import { useIndividualLessonLinkForm } from './components/IndividualLessonLinkFields/useIndividualLessonLinkForm'
import { useGroupPerLessonLinkForm } from './components/GroupPerLessonLinkFields/useGroupPerLessonLinkForm'
import { DEFAULT_MONTHLY_COURSE_PERIOD_UNIT } from './components/MonthlyCourseLinkFields/monthlyCourseLink.utils'
import type { MonthlyCourseLinkPeriodUnit } from './components/MonthlyCourseLinkFields/monthlyCourseLink.utils'
import type { CrmLinkDiscountMode } from './discountMode.utils'
import {
  isCourseLinkGroupSelectableTariff,
  isGroupPerLessonLinkTariff,
} from './tariffTypes'
import { useSelector } from 'react-redux'
import {
  CREATE_LINK_INITIAL_VALUES,
  type CreateLinkFieldType,
} from './createLinkModal.types'
import {
  getCreateLinkSubmitFailedMessages,
  submitCreateLeadPaymentLink,
} from './createLinkSubmit'
import { getCreateLinkTotalPrice } from './createLinkTotalPrice'
import { useCreateLinkFormResets } from './useCreateLinkFormResets'
import CreateLinkModalBody from './CreateLinkModalBody'

interface CreateLinkModalProps {
  lead: LeadProfileLead
  currentLeadFunnelId?: number
  excludedClassIds: number[]
  excludedDirectionIds: number[]
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

const CreateLinkModal: FC<CreateLinkModalProps> = ({
  lead,
  currentLeadFunnelId,
  excludedClassIds = [],
  excludedDirectionIds = [],
  open,
  onClose,
  onSuccess,
}) => {
  const isPromoCodeIsIncludedForPaymentThroughCatalogStore = useSelector(
    (store: any) =>
      store.settings.isPromoCodeIsIncludedForPaymentThroughCatalog,
  )
  const settings = useSelector((store: any) => store.settings)
  const allowSameCourseInDifferentGroups =
    settings?.allowStudentToEnrollSameCourseInDifferentGroups === '1'
  const normalizedExcludedClassIds = useMemo(
    () => excludedClassIds ?? [],
    [excludedClassIds],
  )
  const normalizedExcludedDirectionIds = useMemo(
    () => excludedDirectionIds ?? [],
    [excludedDirectionIds],
  )
  const excludedClassIdsKey = useMemo(
    () => normalizedExcludedClassIds.join(','),
    [normalizedExcludedClassIds],
  )
  const excludedDirectionIdsKey = useMemo(
    () => normalizedExcludedDirectionIds.join(','),
    [normalizedExcludedDirectionIds],
  )

  const classesState = useAsyncRetry(async () => {
    if (!open) return []
    return (
      ((await classApi.index()).data.data ?? []) as ICustomClass[]
    ).filter((item) => {
      return !normalizedExcludedClassIds.includes(item.id)
    })
  }, [open, excludedClassIdsKey])

  const coursesState = useAsync(async () => {
    if (!open) return []
    const res = await directionAPI.getAll({
      filter:
        !allowSameCourseInDifferentGroups &&
        normalizedExcludedDirectionIds.length > 0
          ? [`[direction_id][nin][]=${normalizedExcludedDirectionIds}`]
          : [],
      expand: [
        'directionTariffs',
        'directionTariffs.tariffPeriods',
        'directionTariffs.tariffWeeks',
        'directionTariffs.tariffDays',
      ],
    })
    const dirtyData = (res?.data?.data as ICustomCourse[]) ?? []
    // Скрытые курсы (view=false) тоже в селекте — для CRM-ссылок нужны все.
    return dirtyData
  }, [open, excludedDirectionIdsKey, allowSameCourseInDifferentGroups])

  const addAlert = useAddAlert()
  const [form] = useForm()
  const [submitting, setSubmitting] = useState(false)
  const [isCustomPrice, setIsCustomPrice] = useState(false)
  /** Общий UI-стейт фиксации периода: month / class / stream (активна одна ветка). */
  const [isPeriodFixed, setIsPeriodFixed] = useState(false)

  const typeWatch = useWatch('type', form) as CreateLinkFieldType['type']
  const courseIdWatch = useWatch('course_id', form) as number | null
  const classIdWatch = useWatch('class_id', form) as number | null
  const selectedClass = classesState.value?.find(
    (item) => item.id === classIdWatch,
  )
  const tariffWatch = useWatch('tariff_id', form) as number | null
  const selectedTariff = coursesState.value
    ?.find((course) => course.id === courseIdWatch)
    ?.directionTariffs?.find((tariff) => tariff.id === tariffWatch)
  const canSelectCourseGroup =
    typeWatch === 'course' &&
    isCourseLinkGroupSelectableTariff(selectedTariff?.type)

  const isGroupPerLessonTariff = isGroupPerLessonLinkTariff(selectedTariff?.type)
  const groupIdWatch = useWatch('group_id', form) as number | null

  const courseGroupsState = useAsync(async (): Promise<CourseLinkGroupOption[]> => {
    if (!open || !courseIdWatch) return []

    try {
      const res = await directionGroupApi.get({
        filter: [`[direction_id][eq]=${courseIdWatch}`],
      })
      const groups = (res?.data ?? []) as Array<{
        id?: number
        name?: string | null
        teacher_name?: string | null
        type?: string | null
        group_type?: string | null
        lesson_duration?: number | null
        student_lesson_price?: number | null
        is_shared_balance?: boolean | null
      }>

      return groups
        .filter((group) => {
          if (group?.id == null) return false
          const isLessonPayment =
            group.type === 'lesson-payment' ||
            group.group_type === 'lesson-payment'
          // group_per_lesson — только lesson-payment; остальные тарифы — без них.
          if (isGroupPerLessonTariff) return isLessonPayment
          return !isLessonPayment
        })
        .map((group) => {
          const isSharedBalance = Boolean(group.is_shared_balance)
          const disabled = isGroupPerLessonTariff && isSharedBalance

          return {
            value: Number(group.id),
            label: [group.name, group.teacher_name].filter(Boolean).join(' — '),
            disabled,
            disabledReason: disabled
              ? SHARED_BALANCE_GROUP_DISABLED_TOOLTIP
              : undefined,
            lessonDuration: group.lesson_duration ?? null,
            studentLessonPrice: group.student_lesson_price ?? null,
          }
        })
    } catch (error) {
      return []
    }
  }, [open, courseIdWatch, isGroupPerLessonTariff])

  const selectedCourseGroup = useMemo(() => {
    if (groupIdWatch == null) return null
    return (
      (courseGroupsState.value ?? []).find(
        (item) => item.value === Number(groupIdWatch),
      ) ?? null
    )
  }, [courseGroupsState.value, groupIdWatch])

  const valueWatch = useWatch('value', form) as number | null
  const periodUnitWatch = (useWatch('period_unit', form) ??
    DEFAULT_MONTHLY_COURSE_PERIOD_UNIT) as MonthlyCourseLinkPeriodUnit
  const enrollDateWatch = useWatch('enroll_date', form) as string | null
  const discountWatch = useWatch('discount', form) as number | null
  const discountModeWatch = useWatch('discount_mode', form) as
    | CrmLinkDiscountMode
    | null
  const priceWatch = useWatch('price', form) as number | null
  const firstPriceWatch = useWatch('first_price', form) as number | null
  const isValueFixedWatch = useWatch('is_value_fixed', form) as boolean | null
  const applyDiscountEachPaymentWatch = useWatch(
    'apply_discount_each_payment',
    form,
  ) as boolean
  const additionalCoursesWatch = useWatch('additional_courses', form) as
    | string
    | null

  const monthlyLink = useMonthlyCourseLinkForm({
    form,
    typeWatch,
    selectedTariff,
    selectedClass,
    valueWatch,
    periodUnitWatch,
    discountWatch,
    discountModeWatch,
    enrollDateWatch,
    applyDiscountEachPaymentWatch,
    isPeriodFixed,
    setIsPeriodFixed,
  })

  const streamLink = useStreamCustomPeriodLinkForm({
    form,
    typeWatch,
    selectedTariff,
    valueWatch,
    discountWatch,
    discountModeWatch,
    applyDiscountEachPaymentWatch,
    priceWatch,
    additionalCoursesWatch,
    courses: coursesState.value as { id?: number | null }[] | undefined,
    extraCoursesDiscountsRaw: settings?.extraCoursesDiscounts,
    extraCoursesDiscountUnit: settings?.extraCoursesDiscountUnit,
    isPeriodFixed,
    setIsPeriodFixed,
  })

  const individualLink = useIndividualLessonLinkForm({
    form,
    typeWatch,
    selectedTariff,
    valueWatch,
    firstPriceWatch,
    priceWatch,
    isValueFixedWatch,
    discountWatch,
    additionalCoursesWatch,
    courses: coursesState.value as { id?: number | null }[] | undefined,
    extraCoursesDiscountsRaw: settings?.extraCoursesDiscounts,
    extraCoursesDiscountUnit: settings?.extraCoursesDiscountUnit,
  })

  const oneTimeLink = useStreamOneTimeLinkForm({
    typeWatch,
    selectedTariff,
  })

  const onGroupPerLessonDefaultsApplied = useCallback(() => {
    individualLink.setIsPriceManuallyEdited(false)
  }, [individualLink.setIsPriceManuallyEdited])

  useGroupPerLessonLinkForm({
    form,
    isActive: isGroupPerLessonTariff,
    selectedTariff,
    groupIdWatch,
    selectedCourseGroup,
    onDefaultsApplied: onGroupPerLessonDefaultsApplied,
  })

  useCreateLinkFormResets({
    form,
    open,
    typeWatch,
    courseIdWatch,
    selectedTariff,
    selectedClass,
    isOneTimeActive: oneTimeLink.isActive,
    setIsCustomPrice,
    setIsPeriodFixed,
    setMonthlyPriceManuallyEdited: monthlyLink.setIsPriceManuallyEdited,
    setIndividualPriceManuallyEdited: individualLink.setIsPriceManuallyEdited,
  })

  const selectedCourse = coursesState.value?.find(
    (course) => course.id === courseIdWatch,
  )

  const totalPrice = useMemo(
    () =>
      getCreateLinkTotalPrice({
        streamPackageTotal: streamLink.packageTotal,
        isStreamActive: streamLink.isActive,
        individualTotalPrice: individualLink.totalPrice,
        isIndividualActive: individualLink.isActive,
        selectedTariff,
        valueWatch,
        additionalCoursesWatch,
        courses: coursesState.value,
        extraCoursesDiscountsRaw: settings?.extraCoursesDiscounts,
        extraCoursesDiscountUnit: settings?.extraCoursesDiscountUnit,
      }),
    [
      additionalCoursesWatch,
      coursesState.value,
      individualLink.isActive,
      individualLink.totalPrice,
      selectedTariff,
      settings?.extraCoursesDiscountUnit,
      settings?.extraCoursesDiscounts,
      streamLink.isActive,
      streamLink.packageTotal,
      valueWatch,
    ],
  )

  const handleCustomPriceChange = () => {
    const nextValue = !isCustomPrice
    setIsCustomPrice(nextValue)
    form.setFieldValue('price', nextValue ? totalPrice : null)
  }

  const handlerSubmit: FormProps<CreateLinkFieldType>['onFinish'] = (
    values,
  ) => {
    submitCreateLeadPaymentLink({
      values,
      ctx: {
        currentLeadFunnelId,
        leadId: lead.id,
        selectedTariff,
        selectedClass,
        courses: coursesState.value,
        isPeriodFixed,
        canSelectCourseGroup,
        isGroupPerLessonTariff,
        monthlyCourseBasePrice: monthlyLink.monthlyCourseBasePrice,
        classBasePrice: monthlyLink.classBasePrice,
        isIndividualLinkActive: individualLink.isActive,
      },
      addAlert,
      onSuccess,
      setSubmitting,
    })
  }

  const handlerSubmitFailed: FormProps<CreateLinkFieldType>['onFinishFailed'] =
    (errorInfo) => {
      addAlert({
        title: 'Ошибка валидации',
        text: getCreateLinkSubmitFailedMessages(errorInfo),
        type: 'error',
      })
    }

  const filterSort = useCallback((optionA: any, optionB: any) => {
    return (optionA?.label ?? '')
      .toLowerCase()
      .localeCompare((optionB?.label ?? '').toLowerCase())
  }, [])

  return (
    <Modal
      open={open}
      onOk={onSuccess}
      onCancel={onClose}
      footer={null}
      title={
        <div className={mclasses.modalHeader}>
          <p className={mclasses.modalHeaderTitle}>
            Создание ссылки на покупку
          </p>
          <div className={mclasses.closeIcon} onClick={() => onClose()}>
            <IcoRemove />
          </div>
        </div>
      }
      centered
      maskClosable
      className={cn(
        amclasses.modal,
        sclasses.rubberLayoutMinimized,
        sclasses.disableAdaptRem,
      )}
    >
      <Form
        noValidate
        form={form}
        initialValues={CREATE_LINK_INITIAL_VALUES}
        onFinish={handlerSubmit}
        onFinishFailed={handlerSubmitFailed}
      >
        <div className={sclasses.contentWrapper}>
          <CreateLinkModalBody
            form={form}
            lead={lead}
            typeWatch={typeWatch}
            courseIdWatch={courseIdWatch}
            tariffWatch={tariffWatch}
            valueWatch={valueWatch}
            periodUnitWatch={periodUnitWatch}
            enrollDateWatch={enrollDateWatch}
            discountWatch={discountWatch}
            priceWatch={priceWatch}
            firstPriceWatch={firstPriceWatch}
            selectedCourse={selectedCourse}
            selectedTariff={selectedTariff}
            selectedClass={selectedClass}
            selectedCourseGroup={selectedCourseGroup}
            classesState={classesState}
            coursesState={coursesState}
            courseGroupsState={courseGroupsState}
            canSelectCourseGroup={canSelectCourseGroup}
            isGroupPerLessonTariff={isGroupPerLessonTariff}
            isPeriodFixed={isPeriodFixed}
            isCustomPrice={isCustomPrice}
            totalPrice={totalPrice}
            excludedDirectionIds={excludedDirectionIds}
            isPromoEnabled={
              isPromoCodeIsIncludedForPaymentThroughCatalogStore === '1'
            }
            filterSort={filterSort}
            handleCustomPriceChange={handleCustomPriceChange}
            monthlyLink={monthlyLink}
            streamLink={streamLink}
            individualLink={individualLink}
            oneTimeLinkActive={oneTimeLink.isActive}
          />

          <div className={sclasses.footerActions}>
            <Button
              type="submit"
              color="secondary"
              className={sclasses.submitButton}
              disabled={submitting}
              isLoading={submitting}
            >
              Сохранить
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  )
}

export default CreateLinkModal
